import { Capacitor, CapacitorHttp } from '@capacitor/core';

const imageAsset = `${process.env.PUBLIC_URL || ''}/invites/image-invite.jpeg`;
const pdfAsset = `${process.env.PUBLIC_URL || ''}/invites/pdf-invite.pdf`;
// Reverse-proxied by Apache on the web host to the WhatsApp-Web gateway
// (anuplights.in:8096) so it works from the https page too.
const LIVE_WA_URL = 'https://mehfil.smartifytech.in/wa';
const configuredWaUrl = process.env.REACT_APP_WA_URL || LIVE_WA_URL;
const WA_URL = /^https?:\/\/(localhost|127\.0\.0\.1|\[::1\])(?::|\/|$)/i.test(configuredWaUrl)
  ? LIVE_WA_URL
  : configuredWaUrl;
const UPLOAD_URL = 'https://uploads.anuplights.com/upload';
const DEFAULT_WA_CLIENT = '9172308398';

const clean = (v) => String(v || '').trim();
const ten = (v) => clean(v).replace(/\D/g, '').slice(-10);
const waClientNumber = (v) => {
  const n = clean(v).replace(/\D/g, '');
  if (!n) return '';
  return n.length === 10 ? `91${n}` : n;
};
const waRecipientNumber = (v) => {
  const n = ten(v);
  return n ? `91${n}` : '';
};
const normalizeMediaUrl = (url) => clean(url);
const fileNameFromUrl = (url, fallback = 'invite') => {
  const mediaUrl = normalizeMediaUrl(url);
  if (!mediaUrl) return fallback;
  try {
    const lastSegment = new URL(mediaUrl).pathname.split('/').filter(Boolean).pop();
    return decodeURIComponent(lastSegment || fallback);
  } catch {
    const lastSegment = mediaUrl.split('?')[0].split('#')[0].split('/').filter(Boolean).pop();
    return decodeURIComponent(lastSegment || fallback);
  }
};

export const inviteMediaKey = (kind, event, organiser) => [
  'se_invite_media',
  organiser?.OrganiserId || event?.OrganiserId || 'org',
  event?.EventId || 'event',
  kind,
].join(':');

export const getInviteMedia = (kind, event, organiser) => {
  try {
    return JSON.parse(localStorage.getItem(inviteMediaKey(kind, event, organiser))) || null;
  } catch {
    return null;
  }
};

export const setInviteMedia = (kind, event, organiser, media) => {
  localStorage.setItem(inviteMediaKey(kind, event, organiser), JSON.stringify(media));
};

const firstUrl = (value) => {
  if (!value) return '';
  if (typeof value === 'string') {
    const match = value.match(/https?:\/\/[^\s"'<>]+/);
    if (match) return normalizeMediaUrl(match[0]);
    if (value.startsWith('/uploads/')) return `https://uploads.anuplights.com${value}`;
    return '';
  }
  if (Array.isArray(value)) {
    for (const item of value) {
      const found = firstUrl(item);
      if (found) return found;
    }
    return '';
  }
  if (typeof value === 'object') {
    for (const key of ['fileUrl', 'FileUrl', 'url', 'Url', 'URL', 'path', 'Path', 'FilePath', 'ImageUrl']) {
      const found = firstUrl(value[key]);
      if (found) return found;
    }
    for (const item of Object.values(value)) {
      const found = firstUrl(item);
      if (found) return found;
    }
  }
  return '';
};

export async function uploadInviteFile(file, kind, event, organiser) {
  const form = new FormData();
  form.append('service', 'smartify');
  form.append('file', file);

  const resp = await fetch(UPLOAD_URL, {
    method: 'POST',
    body: form,
  });
  const text = await resp.text();
  let data = text;
  try { data = JSON.parse(text); } catch { /* response may be plain text */ }
  if (!resp.ok) throw new Error(`Upload failed: ${resp.status}`);
  const url = firstUrl(data);
  if (!url) throw new Error('Upload did not return a file URL');
  const media = { url: normalizeMediaUrl(url), name: file.name, kind, uploadedAt: new Date().toISOString() };
  setInviteMedia(kind, event, organiser, media);
  return media;
}

export const inviteTextForGuest = (row, event) => {
  const name = clean([row.Surname, row.GuestName].filter(Boolean).join(' ')) || 'Guest';
  const eventName = clean(event?.EventName) || 'our function';
  const functions = clean(row.FunctionNames || row.FunctionName);
  return [
    `Dear ${name},`,
    `You are warmly invited to ${eventName}${functions ? ` (${functions})` : ''}.`,
    'Please join us and bless the occasion.',
  ].join('\n');
};

async function sendWhatsApp({ phone, message, fileUrl, fileName, clientNumber }) {
  const endpoint = fileUrl ? `${WA_URL}/send-media` : `${WA_URL}/send-message`;
  const mediaUrl = normalizeMediaUrl(fileUrl);
  const mediaFileName = mediaUrl ? fileNameFromUrl(mediaUrl, fileName || 'invite') : '';
  const payload = {
    clientNumber: Number(waClientNumber(clientNumber || DEFAULT_WA_CLIENT)),
    number: Number(waRecipientNumber(phone)),
    message,
    ...(mediaUrl ? {
      ImageUrl: mediaUrl,
      fileName: mediaFileName,
      FileName: mediaFileName,
      filename: mediaFileName,
    } : {}),
  };
  if (!payload.number) throw new Error('Guest mobile number is required');

  if (Capacitor.isNativePlatform()) {
    const resp = await CapacitorHttp.post({
      url: endpoint,
      headers: { 'Content-Type': 'application/json' },
      data: payload,
    });
    if (resp.status < 200 || resp.status >= 300) {
      const detail = typeof resp.data === 'string' ? resp.data : JSON.stringify(resp.data || {});
      throw new Error(`WhatsApp send failed: ${resp.status}${detail ? ` - ${detail}` : ''}`);
    }
    return;
  }

  const resp = await fetch(endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!resp.ok) {
    const detail = await resp.text().catch(() => '');
    throw new Error(`WhatsApp send failed: ${resp.status}${detail ? ` - ${detail}` : ''}`);
  }
}

async function assetToFile(url, fileName, mimeType) {
  const absoluteUrl = new URL(url, window.location.href).toString();
  const res = await fetch(absoluteUrl);
  if (!res.ok) throw new Error(`Could not load invite asset: ${res.statusText}`);
  const blob = await res.blob();
  return new File([blob], fileName, { type: mimeType || blob.type });
}

async function uploadInviteAsset(kind, row, event, organiser) {
  const saved = getInviteMedia(kind, event, organiser);
  if (saved?.url) {
    const normalized = normalizeMediaUrl(saved.url);
    const media = {
      ...saved,
      url: normalized,
      name: saved.name || fileNameFromUrl(normalized, `${kind}-invite`),
    };
    if (normalized !== saved.url || !saved.name) setInviteMedia(kind, event, organiser, media);
    return media;
  }

  const isPdf = kind === 'pdf';
  const file = await assetToFile(
    isPdf ? pdfAsset : imageAsset,
    isPdf ? 'pdf-invite.pdf' : 'image-invite.jpeg',
    isPdf ? 'application/pdf' : 'image/jpeg'
  );
  const media = await uploadInviteFile(file, kind, event || { EventId: row.EventId }, organiser);
  return media;
}

export async function shareInvite(kind, row, event, organiser) {
  const text = inviteTextForGuest(row, event);
  const phone = ten(row.MobileNumber);
  const clientNumber = DEFAULT_WA_CLIENT;
  if (kind === 'text') {
    await sendWhatsApp({ phone, message: text, clientNumber });
    return;
  }

  const media = await uploadInviteAsset(kind, row, event, organiser);
  await sendWhatsApp({ phone, message: text, fileUrl: media.url, fileName: media.name, clientNumber });
}
